import React, { useState } from "react"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import { Group, InputCreateUser } from "lib/types/generated/graphql-types"
import { useMutation } from "@apollo/client"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { useDispatch } from "react-redux"

import CREATE_USER from "graphql/mutations/user/CreateUser"

import EditSidebar from "components/Ui/EditSidebar/EditSidebar"
import EditSidebarHeader from "components/Ui/EditSidebar/EditSidebarHeader"
import PrimaryButton from "components/Ui/Buttons/PrimaryButton"
import EditSidebarSection from "components/Ui/EditSidebar/EditSidebarSection"
import Input from "components/Ui/Form/Input"
import MultiSelect from "../../components/Ui/Form/MultiSelect"
import ADD_USER_TO_GROUP from "../../graphql/mutations/user/AddUserToGroup"
import REMOVE_USER_FROM_GROUP from "../../graphql/mutations/user/RemoveUserFromGroup"
import { hideEditSidebar } from "lib/store/services/editSidebar/slice"
import { ReactComponent as UserIcon } from "images/icons/user-plus.svg"
import { AccessInfo, AccessWrapper } from "./CreateUser.styled"
import { Label } from "./User.styled"

type Props = {
  refetch: () => void
  groupList: Group[]
}

export const CreateUser = ({ refetch, groupList }: Props) => {
  const dispatch = useDispatch()
  const [username, setUserName] = useState("")
  const [addedGroups, setAddedGroups] = useState<string[]>([])

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<InputCreateUser>()

  const closeSidebar = () => {
    setUserName("")
    setAddedGroups([])
    dispatch(hideEditSidebar())
  }

  const [createUser, { loading: createLoading }] = useMutation(CREATE_USER, {
    onCompleted: (data) => {
      setUserName(data.createUser.username)
      reset({
        username: "",
        givenName: "",
        familyName: ""
      })
      refetch()
    },
    onError: (error) => {
      dispatch(
        alertActions.actions.createAlert({
          type: "error",
          message: error.message
        })
      )
    }
  })

  const [addUserToGroup] = useMutation(ADD_USER_TO_GROUP, {
    onError: () => {
      dispatch(
        alertActions.actions.createAlert({
          type: "error"
        })
      )
    }
  })

  const [removeUserFromGroup] = useMutation(REMOVE_USER_FROM_GROUP, {
    onError: () => {
      dispatch(
        alertActions.actions.createAlert({
          type: "error"
        })
      )
    }
  })

  const updateGroups = (value: string[]) => {
    const addedGroup = value.filter((x) => !addedGroups.includes(x))
    const removedGroup = addedGroups.filter((x) => !value.includes(x))
    if (addedGroup.length > 0) {
      addUserToGroup({
        variables: { username: username, groupName: addedGroup[0] }
      })
    }

    if (removedGroup.length > 0) {
      removeUserFromGroup({
        variables: { username: username, groupName: removedGroup[0] }
      })
    }

    setAddedGroups(value)
  }

  const onSubmit: SubmitHandler<InputCreateUser> = (data) => {
    createUser({
      variables: {
        ...data
      }
    })
  }

  const filterNewGroups = groupList
    .filter((group: Group) => !group.name.startsWith("brinkcommerce"))
    .sort((a, b) => a.name.localeCompare(b.name))

  return (
    <EditSidebar cancelEvent={closeSidebar}>
      {username !== "" ? (
        <>
          <EditSidebarHeader icon={<UserIcon />} title="Create new user">   <PrimaryButton handleClick={closeSidebar} disabled={addedGroups.length < 1}>
            Save
          </PrimaryButton></EditSidebarHeader>
          <EditSidebarSection>
            <AccessWrapper>
              <MultiSelect
                label="Access"
                setValue={updateGroups}
                returnValueOnly={true}
                defaultValue={addedGroups.map((g: string) => ({
                  value: g,
                  label: g
                }))}
                options={filterNewGroups
                  .map((g: Group) => ({
                    value: g.name,
                    label: g.name
                  }))}
              />
              {groupList
                ?.filter((group) => addedGroups.includes(group.name))
                ?.map((group) => (
                  <AccessInfo key={group.name}>
                    <Label>{group.name}</Label>
                    <div>{group.description}</div>
                  </AccessInfo>
                ))}
            </AccessWrapper>
          </EditSidebarSection>
        </>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <EditSidebarHeader icon={<UserIcon />} title="Create new user"></EditSidebarHeader>
          <EditSidebarSection>
            <Controller
              name="username"
              render={({ field }) => (
                <Input
                  {...field}
                  type="email"
                  label="Username (Email address) *"
                  placeholder="Username"
                  errors={errors}
                />
              )}
              defaultValue=""
              control={control}
              rules={{
                required: "This is a required field"
              }}
            />
            <Controller
              name="givenName"
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  label="Given name *"
                  placeholder="Given name"
                  errors={errors}
                />
              )}
              defaultValue=""
              control={control}
              rules={{
                required: "This is a required field"
              }}
            />
            <Controller
              name="familyName"
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  label="Family name *"
                  placeholder="Family name"
                  errors={errors}
                />
              )}
              defaultValue=""
              control={control}
              rules={{
                required: "This is a required field"
              }}
            />
            <PrimaryButton loading={createLoading} type="submit">
              Continue to access
            </PrimaryButton>
          </EditSidebarSection>
        </form>
      )}
    </EditSidebar>
  )
}
